import { useEffect, useState, useCallback, useRef } from 'react';
import { http } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Link, Navigate } from 'react-router-dom';
import {
  Users, ArrowLeft, RefreshCw, Trash2, ShieldCheck, Search, Loader2,
} from 'lucide-react';

function fmt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState(null);       // { kind: 'reset'|'delete', target }
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [hiddenIds, setHiddenIds] = useState(() => new Set()); // optimistically hidden during the undo window
  const pendingRef = useRef(new Map()); // token -> { ids, timer }

  // Clear any in-flight undo timers on unmount.
  useEffect(() => () => { pendingRef.current.forEach((e) => clearTimeout(e.timer)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get('/admin/users');
      setUsers(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('Failed to load users');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = (q
    ? users.filter(u => (u.email || '').toLowerCase().includes(q.toLowerCase())
                      || (u.name || '').toLowerCase().includes(q.toLowerCase()))
    : users).filter(u => !hiddenIds.has(u.id));

  const doReset = async (target) => {
    setBusy(true);
    try {
      const r = await http.post(`/admin/users/${target.id}/reset`);
      toast.success(`Reset — ${r.data?.purged_total || 0} rows purged`);
      setDialog(null); setConfirmText('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  const doDelete = async (target, confirmation) => {
    setBusy(true);
    try {
      const r = await http.delete(`/admin/users/${target.id}`, { data: { confirmation } });
      toast.success(`Deleted ${r.data?.deleted} — ${r.data?.purged_total || 0} rows purged`);
      setDialog(null); setConfirmText('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const doPurge = async (confirmation) => {
    setBusy(true);
    try {
      const r = await http.delete('/admin/users/purge-non-admin', { data: { confirmation } });
      toast.success(`Purged ${r.data?.users_deleted || 0} non-admin users (${r.data?.rows_purged || 0} rows)`);
      setDialog(null); setConfirmText('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Purge failed');
    } finally { setBusy(false); }
  };

  // Bulk delete with a short UNDO window: rows are hidden immediately and the
  // actual API call is deferred ~6s so the admin can undo before it's finalized.
  const UNDO_MS = 6000;

  const finalizeBulkDelete = useCallback(async (token, ids) => {
    pendingRef.current.delete(token);
    try {
      const r = await http.post('/admin/users/bulk-delete', { ids, confirmation: 'DELETE' });
      const n = r.data?.deleted_count || 0;
      const skipped = r.data?.skipped?.length || 0;
      toast.success(`Deleted ${n} user${n === 1 ? '' : 's'} (${r.data?.purged_total || 0} rows)${skipped ? ` · ${skipped} skipped` : ''}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setHiddenIds((prev) => { const nx = new Set(prev); ids.forEach((i) => nx.delete(i)); return nx; });
    }
  }, [load]);

  const undoBulkDelete = useCallback((token) => {
    const entry = pendingRef.current.get(token);
    if (!entry) return;
    clearTimeout(entry.timer);
    pendingRef.current.delete(token);
    setHiddenIds((prev) => { const nx = new Set(prev); entry.ids.forEach((i) => nx.delete(i)); return nx; });
    toast.success('Restored — nothing was deleted');
  }, []);

  const scheduleBulkDelete = () => {
    const ids = [...selected];
    if (!ids.length) return;
    setDialog(null); setConfirmText(''); setSelected(new Set());
    setHiddenIds((prev) => new Set([...prev, ...ids]));
    const token = `${Date.now()}-${Math.random()}`;
    const timer = setTimeout(() => finalizeBulkDelete(token, ids), UNDO_MS);
    pendingRef.current.set(token, { ids, timer });
    toast(`Deleting ${ids.length} user${ids.length === 1 ? '' : 's'}…`, {
      duration: UNDO_MS,
      action: { label: 'Undo', onClick: () => undoBulkDelete(token) },
    });
  };

  // Selection helpers — self cannot be selected (can't delete yourself)
  const selectableIds = filtered.filter((u) => u.id !== user?.id).map((u) => u.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(selectableIds));

  const nonAdminCount = users.filter((u) => !u.is_admin).length;

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-5 lg:p-10 space-y-6" data-testid="admin-users-page">
      <Link to="/app/admin" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="back-to-admin">
        <ArrowLeft size={14}/> Back to Admin
      </Link>

      <div className="flex items-center gap-3">
        <Users className="text-gold" size={22}/>
        <div>
          <h1 className="font-heading text-3xl">User Management</h1>
          <p className="text-sm text-navy/55">Reset user data or delete accounts. Admin actions are audit-logged.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 card-soft flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40"/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or email…"
            className="w-full pl-9 pr-3 py-2 rounded-lg ring-1 ring-navy/10 focus:ring-navy outline-none text-sm"
            data-testid="admin-users-search"
          />
        </div>
        <button onClick={load} className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy/90 transition-smooth flex items-center gap-2" data-testid="admin-users-refresh">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      <div className="bg-white rounded-2xl overflow-hidden card-soft">
        {selected.size > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-50 border-b border-red-200" data-testid="bulk-action-bar">
            <span className="text-sm font-medium text-navy" data-testid="bulk-selected-count">{selected.size} selected</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected(new Set())}
                className="px-3 py-1.5 rounded-lg text-sm text-navy/70 hover:bg-navy/5"
                data-testid="bulk-clear-btn"
              >Clear</button>
              <button
                onClick={() => { setDialog({ kind: 'bulk-delete' }); setConfirmText(''); }}
                className="px-4 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 inline-flex items-center gap-2"
                data-testid="bulk-delete-btn"
              ><Trash2 size={14}/> Delete {selected.size} selected</button>
            </div>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-navy/[0.03] text-[11px] uppercase tracking-widest text-navy/55">
            <tr>
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-navy/30 accent-red-600 cursor-pointer"
                  aria-label="Select all"
                  data-testid="select-all-checkbox"
                />
              </th>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Registered</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-navy/45"><Loader2 className="inline animate-spin" size={16}/></td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-navy/45">No users match.</td></tr>
            )}
            {filtered.map((u, idx) => {
              const isMe = u.id === user.id;
              const paidBadge = (u.subscription_status || '').toLowerCase() === 'active'
                ? { txt: 'Active', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' }
                : { txt: 'Unpaid', cls: 'bg-navy/5 text-navy/60 ring-navy/10' };
              return (
                <tr key={u.id} className={`border-t border-navy/5 hover:bg-navy/[0.02] ${selected.has(u.id) ? 'bg-red-50/60' : ''}`} data-testid={`admin-user-row-${idx}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      disabled={isMe}
                      onChange={() => toggleOne(u.id)}
                      className="w-4 h-4 rounded border-navy/30 accent-red-600 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title={isMe ? 'You cannot delete yourself' : 'Select'}
                      aria-label={`Select ${u.email}`}
                      data-testid={`select-checkbox-${idx}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{u.name || '—'}</div>
                    <div className="text-[11px] text-navy/50">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {u.is_admin && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ring-1 bg-gold/10 text-gold ring-gold/30 mr-1"><ShieldCheck size={10}/>Admin</span>
                    )}
                    <span className="text-[11px] text-navy/70">{u.role || 'member'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ring-1 ${paidBadge.cls}`}>
                      {paidBadge.txt}
                    </span>
                    {u.email_verified ? null : (
                      <span className="ml-1 inline-block px-2 py-0.5 rounded-full text-[10px] bg-amber-50 text-amber-700 ring-1 ring-amber-200">Unverified</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-navy/60">{fmt(u.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => { setDialog({ kind: 'reset', target: u }); setConfirmText(''); }}
                      disabled={u.is_admin}
                      className="text-[12px] text-navy/70 hover:text-navy hover:underline mr-3 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={u.is_admin ? 'Cannot reset admin accounts' : 'Wipe activity but keep the account'}
                      data-testid={`reset-btn-${idx}`}
                    >Reset data</button>
                    <button
                      onClick={() => { setDialog({ kind: 'delete', target: u }); setConfirmText(''); }}
                      disabled={isMe}
                      className="text-[12px] text-red-600 hover:text-red-700 hover:underline disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      title={isMe ? 'You cannot delete yourself' : 'Permanently delete'}
                      data-testid={`delete-btn-${idx}`}
                    ><Trash2 size={12}/>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Danger Zone — bulk purge of all non-admin users */}
      <div className="bg-white rounded-2xl p-5 card-soft border border-red-200" data-testid="admin-users-danger-zone">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center shrink-0"><Trash2 size={16} className="text-red-600"/></div>
          <div className="flex-1">
            <h2 className="font-heading text-lg text-red-700">Danger zone</h2>
            <p className="text-[13px] text-navy/60 mt-1 leading-relaxed">
              Permanently delete <span className="font-semibold">every non-admin user</span> ({nonAdminCount}) and all of their data — useful to clear stale test/beta accounts before launch. Admin accounts are always kept. This cannot be undone and is audit-logged.
            </p>
          </div>
          <button
            onClick={() => { setDialog({ kind: 'purge' }); setConfirmText(''); }}
            disabled={nonAdminCount === 0}
            className="shrink-0 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="purge-non-admin-btn"
          ><Trash2 size={14}/> Purge non-admins</button>
        </div>
      </div>

      {/* Confirmation dialog */}
      {dialog && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" role="dialog">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" data-testid={`confirm-dialog-${dialog.kind}`}>
            {dialog.kind === 'bulk-delete' ? (
              <>
                <h2 className="font-heading text-xl text-red-700">Delete {selected.size} selected user{selected.size === 1 ? '' : 's'}?</h2>
                <p className="mt-2 text-sm text-navy/70">
                  This permanently deletes the selected account{selected.size === 1 ? '' : 's'} and all of their data. Your own account and the last remaining admin are always kept. This cannot be undone and is audit-logged.
                </p>
                <p className="mt-4 text-[12px] uppercase tracking-widest text-navy/50">Type <span className="font-mono text-red-600">DELETE</span> to confirm:</p>
                <input
                  autoFocus
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="mt-2 w-full px-3 py-2 rounded-lg ring-1 ring-red-200 focus:ring-red-500 outline-none text-sm font-mono"
                  data-testid="bulk-delete-confirm-input"
                />
                <div className="mt-6 flex items-center justify-end gap-2">
                  <button onClick={() => { setDialog(null); setConfirmText(''); }} className="px-4 py-2 rounded-lg text-sm text-navy/70 hover:bg-navy/5" data-testid="dialog-cancel">Cancel</button>
                  <button
                    onClick={() => scheduleBulkDelete()}
                    disabled={busy || confirmText.trim() !== 'DELETE'}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="confirm-bulk-delete-btn"
                  >{busy ? 'Deleting…' : `Delete ${selected.size} user${selected.size === 1 ? '' : 's'}`}</button>
                </div>
              </>
            ) : dialog.kind === 'purge' ? (
              <>
                <h2 className="font-heading text-xl text-red-700">Purge ALL non-admin users?</h2>
                <p className="mt-2 text-sm text-navy/70">
                  This permanently deletes <span className="font-semibold">{nonAdminCount}</span> user{nonAdminCount === 1 ? '' : 's'} and every bit of their data. Admin accounts are preserved.
                </p>
                <p className="mt-4 text-[12px] uppercase tracking-widest text-navy/50">Type <span className="font-mono text-red-600">PURGE</span> to confirm:</p>
                <input
                  autoFocus
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="PURGE"
                  className="mt-2 w-full px-3 py-2 rounded-lg ring-1 ring-red-200 focus:ring-red-500 outline-none text-sm font-mono"
                  data-testid="purge-confirm-input"
                />
                <div className="mt-6 flex items-center justify-end gap-2">
                  <button onClick={() => { setDialog(null); setConfirmText(''); }} className="px-4 py-2 rounded-lg text-sm text-navy/70 hover:bg-navy/5" data-testid="dialog-cancel">Cancel</button>
                  <button
                    onClick={() => doPurge(confirmText)}
                    disabled={busy || confirmText.trim() !== 'PURGE'}
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="confirm-purge-btn"
                  >{busy ? 'Purging…' : `Purge ${nonAdminCount} users`}</button>
                </div>
              </>
            ) : (
            <>
            <h2 className="font-heading text-xl">
              {dialog.kind === 'reset' ? 'Reset user data?' : 'Delete this user?'}
            </h2>
            <p className="mt-2 text-sm text-navy/70">
              <span className="font-medium">{dialog.target.name || dialog.target.email}</span>
              {' — '}<span className="text-navy/50">{dialog.target.email}</span>
            </p>
            {dialog.kind === 'reset' ? (
              <p className="mt-3 text-[13px] text-navy/60 leading-relaxed">
                This wipes their assessments, journals, chats, progress, check-ins, notifications, and sessions. The account itself stays. They&apos;ll be signed out and can log back in.
              </p>
            ) : (
              <>
                <p className="mt-3 text-[13px] text-navy/60 leading-relaxed">
                  This permanently deletes the user AND all their data.
                </p>
                <p className="mt-4 text-[12px] uppercase tracking-widest text-navy/50">Type <span className="font-mono text-red-600">DELETE</span> or the user&apos;s email to confirm:</p>
                <input
                  autoFocus
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="mt-2 w-full px-3 py-2 rounded-lg ring-1 ring-red-200 focus:ring-red-500 outline-none text-sm font-mono"
                  data-testid="delete-confirm-input"
                />
              </>
            )}
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => { setDialog(null); setConfirmText(''); }}
                className="px-4 py-2 rounded-lg text-sm text-navy/70 hover:bg-navy/5"
                data-testid="dialog-cancel"
              >Cancel</button>
              {dialog.kind === 'reset' ? (
                <button
                  onClick={() => doReset(dialog.target)}
                  disabled={busy}
                  className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy/90 disabled:opacity-50"
                  data-testid="confirm-reset-btn"
                >{busy ? 'Resetting…' : 'Reset data'}</button>
              ) : (
                <button
                  onClick={() => doDelete(dialog.target, confirmText)}
                  disabled={busy || (confirmText.trim() !== 'DELETE' && confirmText.trim().toLowerCase() !== (dialog.target.email || '').toLowerCase())}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="confirm-delete-btn"
                >{busy ? 'Deleting…' : 'Delete permanently'}</button>
              )}
            </div>
            </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
